import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Send, FileDown, Loader2, Building, Calendar, User, Truck, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const statusLabels = {
  draft: 'Taslak',
  sent: 'Gönderildi',
  partial: 'Kısmi Teslim',
  received: 'Teslim Alındı',
  completed: 'Tamamlandı',
  cancelled: 'İptal'
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-purple-100 text-purple-800',
  partial: 'bg-orange-100 text-orange-800',
  received: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

export default function PurchaseOrderDetail() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('purchasing', 'delete');
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/purchasing');
  const [order, setOrder] = useState(null);
  usePageTitle('Sipariş Detayı');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [suppliers, setSuppliers] = useState({});
  const [silinsinMi, setSilinsinMi] = useState(false);

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      const r = await api.delete(`/purchase-orders/${id}`);
      toast.success(r.data?.talep_geri_alindi
        ? 'Sipariş silindi, talep onaylı duruma döndü'
        : 'Sipariş silindi');
      navigate('/erp/purchase-orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sipariş silinemedi');
    } finally {
      setActionLoading(false);
      setSilinsinMi(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const [orderRes, suppliersRes] = await Promise.all([
        api.get(`/purchase-orders/${id}`),
        api.get('/suppliers')
      ]);
      setOrder(orderRes.data);
      // Create supplier lookup
      const supplierMap = {};
      (suppliersRes.data || []).forEach(s => { supplierMap[s.id] = s; });
      setSuppliers(supplierMap);
    } catch (err) {
      toast.error('Sipariş yüklenemedi');
      navigate('/erp/purchasing');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setActionLoading(true);
    try {
      await api.patch(`/purchase-orders/${id}/send`);
      toast.success('Sipariş gönderildi ve mal kabul bekliyor');
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/purchase-orders/${id}/pdf-data`);
      const data = res.data;
      
      // Generate PDF for each supplier
      data.supplier_orders.forEach((supplierOrder, index) => {
        generatePDF(data, supplierOrder, index + 1);
      });
      
      toast.success(`${data.supplier_orders.length} adet PDF oluşturuldu`);
    } catch (err) {
      toast.error('PDF oluşturulamadı');
    } finally {
      setPdfLoading(false);
    }
  };

  const generatePDF = (orderData, supplierOrder, index) => {
    // Create a printable HTML and open in new window
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sipariş Formu - ${supplierOrder.supplier_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .company-info { }
          .company-name { font-size: 24px; font-weight: bold; color: #1a1916; }
          .company-address { font-size: 12px; color: #666; margin-top: 5px; }
          .order-info { text-align: right; }
          .order-no { font-size: 18px; font-weight: bold; }
          .order-date { color: #666; margin-top: 5px; }
          .supplier-section { background: #f5f5f5; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
          .supplier-title { font-size: 12px; color: #666; margin-bottom: 5px; }
          .supplier-name { font-size: 18px; font-weight: bold; }
          .supplier-details { font-size: 12px; color: #666; margin-top: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #2e2d29; color: white; padding: 12px; text-align: left; font-size: 12px; }
          td { padding: 12px; border-bottom: 1px solid #ddd; }
          .text-right { text-align: right; }
          .total-row { font-weight: bold; background: #f5f5f5; }
          .footer { margin-top: 50px; display: flex; justify-content: space-between; }
          .signature-box { width: 200px; text-align: center; }
          .signature-line { border-top: 1px solid #333; margin-top: 60px; padding-top: 10px; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <div class="company-name">${orderData.company_name}</div>
            <div class="company-address">${orderData.company_address || ''}</div>
          </div>
          <div class="order-info">
            <div class="order-no">Sipariş No: ${orderData.order_no}</div>
            <div class="order-date">Tarih: ${new Date(orderData.order_date).toLocaleDateString('tr-TR')}</div>
          </div>
        </div>
        
        <div class="supplier-section">
          <div class="supplier-title">TEDARİKÇİ</div>
          <div class="supplier-name">${supplierOrder.supplier_name}</div>
          <div class="supplier-details">
            ${supplierOrder.supplier_address ? `<div>${supplierOrder.supplier_address}</div>` : ''}
            ${supplierOrder.supplier_phone ? `<div>Tel: ${supplierOrder.supplier_phone}</div>` : ''}
            ${supplierOrder.supplier_email ? `<div>E-posta: ${supplierOrder.supplier_email}</div>` : ''}
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Ürün Adı</th>
              <th class="text-right">Miktar</th>
              <th>Birim</th>
              <th class="text-right">Birim Fiyat</th>
              <th class="text-right">Tutar</th>
            </tr>
          </thead>
          <tbody>
            ${supplierOrder.items.map((item, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${item.product_name}</td>
                <td class="text-right">${item.quantity}</td>
                <td>${item.unit}</td>
                <td class="text-right">${item.unit_price.toFixed(2)} TRY</td>
                <td class="text-right">${item.total_price.toFixed(2)} TRY</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="5" class="text-right">TOPLAM:</td>
              <td class="text-right">${supplierOrder.total.toFixed(2)} TRY</td>
            </tr>
          </tbody>
        </table>
        
        <div class="footer">
          <div class="signature-box">
            <div class="signature-line">Sipariş Veren</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Onaylayan</div>
          </div>
        </div>
        
        <script>window.print();</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (!order) return null;

  // Group items by supplier
  const itemsBySupplier = {};
  (order.items || []).forEach(item => {
    const sid = item.supplier_id || order.supplier_id;
    if (!itemsBySupplier[sid]) itemsBySupplier[sid] = [];
    itemsBySupplier[sid].push(item);
  });

  return (
    <div className="p-6 space-y-6" data-testid="purchase-order-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1a1916]">Sipariş Detayı</h1>
            <p className="text-sm text-[#8a7e70]">#{order.order_no || order.id?.slice(0, 8)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={statusColors[order.status]}>{statusLabels[order.status]}</Badge>
          {/* Gönderilmemiş sipariş: düzenlenebilir ve silinebilir */}
          {order.status === 'draft' && (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/erp/purchase-orders/${id}/edit`)}
                disabled={actionLoading}
                className="border-[#d0c4b0] text-[#3F3E39]"
                data-testid="edit-order-btn"
              >
                <Pencil size={16} className="mr-2" /> Düzenle
              </Button>
              {silmeYetkisi && (
              <Button
                variant="outline"
                onClick={() => setSilinsinMi(true)}
                disabled={actionLoading}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="delete-order-btn"
              >
                <Trash2 size={16} className="mr-2" /> Sil
              </Button>
              )}
              <Button
                onClick={handleSend}
                disabled={actionLoading}
                className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
                data-testid="send-order-btn"
              >
                {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                Siparişi Gönder
              </Button>
            </>
          )}
          <Button 
            variant="outline"
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            className="border-[#d0c4b0]"
            data-testid="download-pdf-btn"
          >
            {pdfLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <FileDown size={16} className="mr-2" />}
            PDF İndir
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#e8dfd0] rounded-lg">
                <Calendar size={20} className="text-[#3d6e4e]" />
              </div>
              <div>
                <p className="text-xs text-[#8a7e70]">Sipariş Tarihi</p>
                <p className="font-semibold text-[#1a1916]">
                  {new Date(order.created_at).toLocaleDateString('tr-TR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#e8dfd0] rounded-lg">
                <Truck size={20} className="text-[#3d6e4e]" />
              </div>
              <div>
                <p className="text-xs text-[#8a7e70]">Beklenen Teslim</p>
                <p className="font-semibold text-[#1a1916]">
                  {order.expected_delivery_date ? new Date(order.expected_delivery_date).toLocaleDateString('tr-TR') : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#e8dfd0] rounded-lg">
                <Building size={20} className="text-[#3d6e4e]" />
              </div>
              <div>
                <p className="text-xs text-[#8a7e70]">Ana Tedarikçi</p>
                <p className="font-semibold text-[#1a1916]">{order.supplier_name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#3d6e4e] rounded-lg">
                <span className="text-white font-bold text-sm">TRY</span>
              </div>
              <div>
                <p className="text-xs text-[#8a7e70]">Toplam Tutar</p>
                <p className="font-bold text-[#1a1916] text-lg">
                  {(order.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items by Supplier */}
      {Object.entries(itemsBySupplier).map(([supplierId, items]) => {
        const supplier = suppliers[supplierId];
        const supplierTotal = items.reduce((sum, i) => sum + (i.total_price || 0), 0);
        
        return (
          <Card key={supplierId} className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="border-b border-[#d0c4b0]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
                  <Building size={18} />
                  {supplier?.name || 'Tedarikçi'}
                </CardTitle>
                <span className="text-sm text-[#8a7e70]">
                  Alt Toplam: <span className="font-bold text-[#1a1916]">{supplierTotal.toFixed(2)} TRY</span>
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#e8dfd0]">
                    <TableHead className="text-[#1a1916] font-bold">Ürün Adı</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-right">Miktar</TableHead>
                    <TableHead className="text-[#1a1916] font-bold">Birim</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-right">Birim Fiyat</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-right">Tutar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, i) => (
                    <TableRow key={i} className="hover:bg-[#f5f0e8]">
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell className="text-right font-semibold">{item.quantity}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell className="text-right font-mono">{(item.unit_price || 0).toFixed(2)} TRY</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{(item.total_price || 0).toFixed(2)} TRY</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      {/* Notes */}
      {order.notes && (
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Notlar</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-[#3F3E39]">{order.notes}</p>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={silinsinMi} onOpenChange={setSilinsinMi}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sipariş silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {order.order_no || 'Sipariş'} ({order.items?.length || 0} kalem,{' '}
              {order.total_amount?.toFixed(2)} TL) silinecek. Bu işlem geri alınamaz.
              {order.purchase_request_id && ' Bağlı talep onaylı duruma geri döner.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}
              className="bg-[#7a3535] hover:bg-[#5e2929]" data-testid="confirm-delete-order">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
